import React, { useState } from "react";
import { Row, Col, Card, Tag, Checkbox, Skeleton, Spin } from "antd";
import VideosPlayer from "./VideosPlayer";
import {
  LikeOutlined,
  StarFilled,
  CheckOutlined,
  CloseOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { CustomButton, CustomSecondaryButton } from "../physician_page/styled";
import { FiPlay } from "react-icons/fi";
import styled from "styled-components";
import { capitalizeFirstLetter, formatDuration } from "../../../utils/utils";

// Enhanced styled components for visual appeal
const StyledCard = styled(Card)`
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: #ffffff;
  position: relative;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 35px -5px rgba(0, 0, 0, 0.15),
      0 15px 15px -5px rgba(0, 0, 0, 0.08);
  }

  .ant-card-body {
    padding: 24px;
  }

  .ant-card-cover {
    position: relative;
    overflow: hidden;

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 60px;
      background: linear-gradient(to top, rgba(0, 0, 0, 0.3), transparent);
      pointer-events: none;
    }
  }
`;

const VideoTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-text-color);
  margin-bottom: 8px;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const VideoDescription = styled.p`
  font-size: 15px;
  color: var(--secondary-text-color);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 16px;
  font-weight: 600;
`;

const MetricsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-top: 1px solid #f3f4f6;
  margin-top: 16px;
`;

const RatingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LikeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: ${(props) => (props.isApproval ? "center" : "flex-end")};
  gap: 12px;
  margin-top: 20px;
`;

const VideoContainer = ({
  videos,
  isPersonalize,
  handlePersonalize,
  isApproval,
  handleApprove,
  handleReject,
  isAttachRecording,
  handleOpenModal,
  selectedVideos,
  onSelectVideo,
  onlyMasterVideo = false,
  isLoading,
}) => {
  const getVideoData = (video) => {
    if (isApproval) {
      return {
        id: video?.videoId?.id || video?.id,
        title: video?.videoId?.title || "Untitled Video",
        description: video?.videoId?.description || "",
        videoUrl: video?.videoId?.videoUrl || "",
        specialty: video?.videoId?.specialty || "",
        referenceMasterVideoId: video?.videoId?.referenceMasterVideoId || null,
        ...video
      };
    }
    return {
      id: video?.id || "",
      title: video?.title || "Untitled Video",
      description: video?.description || "",
      videoUrl: video?.videoUrl || "",
      tags: video?.tags || [],
      specialty: video?.specialty || "",
      referenceMasterVideoId: video?.referenceMasterVideoId || null,
      ...video
    };
  };

  // Get the videos array based on the data structure
  const videoList = isApproval ? videos : videos?.results || [];

  // Filter the video list based on the onlyMasterVideo prop
  const filteredVideoList = onlyMasterVideo ? videoList.filter(video => !video.referenceMasterVideoId) : videoList;


  return (
    <Row gutter={[24, 24]}>
      {filteredVideoList?.map((video, i) => {
        const videoData = getVideoData(video);
        
      

        return (
          <Col lg={8} md={12} sm={24} xs={24} key={videoData.id || i}>
            {isLoading ? (
              <Skeleton.Input active style={{width: 400, height: 300, borderRadius: "10px"}} />
            ) : (
            <StyledCard
              cover={
                <VideosPlayer
                  videoUrl={videoData.videoUrl}
                  title={videoData.title}
                />
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <VideoTitle style={{ marginBottom: 0 }}>
                  {capitalizeFirstLetter(videoData.title)}
                </VideoTitle>
              </div>
              <VideoDescription style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <Tag>{capitalizeFirstLetter(videoData.specialty) || "Endocrinology"}</Tag>


                <Tag color={!videoData?.generalLibrary ? videoData.referenceMasterVideoId ? "orange" : "purple" : 'green'}>
                {videoData?.videoType === 'ccm' ? "CCM" : 
                !videoData?.generalLibrary ? videoData.referenceMasterVideoId ? "AI Enhanced" : "Master" : 'Patient Education'}
                </Tag>


                {videoData?.duration && (
                  <Tag>
                    {formatDuration(videoData.duration)} <ClockCircleOutlined/>
                  </Tag>
                )}
              </VideoDescription>

              <VideoDescription>
                {videoData.tags?.map((tag) => (
                  <Tag key={tag}>{tag}</Tag>
                ))}
              </VideoDescription>



              {isPersonalize && (
                <ButtonContainer>
                  <CustomButton
                    onClick={() => {
                      handlePersonalize(videoData.videoUrl);
                    }}
                    style={{ width: "100%" }}
                  >
                    Assign to Patient
                  </CustomButton>
                </ButtonContainer>
              )}

              {isAttachRecording && (
                <ButtonContainer isApproval>
                  <CustomButton
                    onClick={() => handleOpenModal(videoData)}
                    style={{ flex: 1 }}
                  >
                    Select
                  </CustomButton>
                </ButtonContainer>
              )}
              {isApproval && (
                <ButtonContainer isApproval>
                  <CustomSecondaryButton
                    icon={<CheckOutlined />}
                    onClick={() => handleApprove(videoData.id)}
                    style={{ flex: 1 }}
                  >
                    Approve
                  </CustomSecondaryButton>
                  <CustomButton
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => handleReject(videoData.id)}
                    style={{ flex: 1 }}
                  >
                    Reject
                  </CustomButton>
                </ButtonContainer>
              )}
            </StyledCard>
            )}
          </Col>
        );
      })}
    </Row>
  );
};

export default VideoContainer;
